import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { Autocomplete, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import LoadingSpinner from "components/LoadingSpinner";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import { MecordColors } from "Constants/Color";
import { RootState } from "../../../../index";
import {
    fetchAccessoryReturnEditData,
    updateAccessoryReturn,
} from "slices/sales_order/challan/thunk";
import { clearChallanState } from "slices/sales_order/challan/reducer";

type ReturnUnitOption = {
    inventory_product_id: string;
    serial_no: string;
    warranty_date?: string | null;
};

type FlatRow = {
    key: string;
    unitIdx: number;
    globalIdx: number;
    checked: boolean;
    inventory_product_id: string;
    serial_no: string;
    warranty_date?: string | null;
    // display info
    product_name: string;
    product_sku: string;
    service_name: string;
    service_sku: string;
    service_sn: string;
    issued_qty: number;
    other_returned_qty: number;
    existing_qty: number;
    remaining_qty: number;
    available: ReturnUnitOption[];
};

const accessoryKey = (itemId: string, accessory: any) =>
    `${itemId}::${accessory?.accessory_id || accessory?.product_id || ""}`;

const getExistingSelectedIds = (
    returnRecord: any,
    itemId: string,
    acc: any,
): string[] => {
    if (!returnRecord?.items) return [];
    for (const item of returnRecord.items) {
        if (item.sales_order_service_item_id !== itemId) continue;
        const found = (item.accessories || []).find(
            (a: any) =>
                (a.accessory_id && a.accessory_id === acc.accessory_id) ||
                (a.product_id && a.product_id === acc.product_id),
        );
        if (!found) continue;
        const ids = Array.isArray(found.return_inventory_product_ids)
            ? found.return_inventory_product_ids
            : Array.isArray(found.issued_inventory_product_ids)
              ? found.issued_inventory_product_ids
              : [];
        const qty = Number(found.return_qty || 0);
        const cleaned = ids.filter(Boolean).map(String);
        if (qty > 0 && cleaned.length > qty) return cleaned.slice(0, qty);
        return cleaned;
    }
    return [];
};

const EditReturnAccessories: React.FC = () => {
    document.title = "Edit Accessory Return";

    const { challanId, returnId } = useParams<{ challanId: string; returnId: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch<any>();
    const { accessoryReturnEditData, loading } = useSelector((state: RootState) => state.Challan);

    const [flatRows, setFlatRows] = useState<FlatRow[]>([]);
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        setInitialized(false);
        setFlatRows([]);
        setNote("");
        if (challanId && returnId) {
            dispatch(fetchAccessoryReturnEditData({ challanId, returnId }));
        }
        return () => {
            dispatch(clearChallanState());
        };
    }, [dispatch, challanId, returnId]);

    // Build flat rows from API data
    useEffect(() => {
        if (!accessoryReturnEditData || initialized) return;

        const returnRecord = accessoryReturnEditData.return_record;
        const rows: FlatRow[] = [];
        let globalIdx = 0;

        (accessoryReturnEditData.services || []).forEach((service: any) => {
            (service.accessories || []).forEach((acc: any) => {
                const key = accessoryKey(service.sales_order_service_item_id, acc);
                const maxQty = Math.max(0, Number(acc.remaining_qty || 0));
                const available: ReturnUnitOption[] = Array.isArray(acc.available_return_units)
                    ? acc.available_return_units
                    : [];
                const existingIds = getExistingSelectedIds(
                    returnRecord,
                    service.sales_order_service_item_id,
                    acc,
                );

                for (let i = 0; i < maxQty; i++) {
                    const id = existingIds[i] || "";
                    const match = id ? available.find((u) => u.inventory_product_id === id) : null;
                    rows.push({
                        key,
                        unitIdx: i,
                        globalIdx: ++globalIdx,
                        checked: !!id,
                        inventory_product_id: id,
                        serial_no: match?.serial_no || "",
                        warranty_date: match?.warranty_date || null,
                        product_name: acc.product_name || "-",
                        product_sku: acc.product_sku || "",
                        service_name: service.product_name || "Service",
                        service_sku: service.product_sku || "",
                        service_sn: service.serial_number || "",
                        issued_qty: Number(acc.issued_qty || 0),
                        other_returned_qty: Number(acc.other_returned_qty || 0),
                        existing_qty: existingIds.length,
                        remaining_qty: maxQty,
                        available,
                    });
                }
            });
        });

        setNote(returnRecord?.note || "");
        setFlatRows(rows);
        setInitialized(true);
    }, [accessoryReturnEditData, initialized]);

    const viewPath = challanId
        ? `/sales_order/service/challan/return-view/${challanId}`
        : "/sales_order/service/sales_order_log";
    const listingPath = accessoryReturnEditData?.sales_order_service_id
        ? `/sales_order/service/challan/${accessoryReturnEditData.sales_order_service_id}`
        : "/sales_order/service/sales_order_log";

    const thStyle: React.CSSProperties = {
        padding: "10px 12px",
        borderBottom: "2px solid #eee",
        textAlign: "left",
        fontSize: "0.78rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        backgroundColor: MecordColors.LightGrayColor,
    };
    const tdStyle: React.CSSProperties = {
        padding: "10px 12px",
        borderBottom: "1px solid #f0f0f0",
        fontSize: "0.78rem",
        verticalAlign: "middle",
    };

    const totalReturnQty = useMemo(
        () => flatRows.filter((r) => r.checked && r.inventory_product_id).length,
        [flatRows],
    );

    const updateRow = (globalIdx: number, patch: Partial<FlatRow>) => {
        setFlatRows((prev) =>
            prev.map((r) => (r.globalIdx === globalIdx ? { ...r, ...patch } : r)),
        );
    };

    const selectedIdsForKey = (key: string, excludeGlobalIdx: number): Set<string> => {
        const ids = new Set<string>();
        flatRows.forEach((r) => {
            if (r.key === key && r.globalIdx !== excludeGlobalIdx && r.inventory_product_id) {
                ids.add(r.inventory_product_id);
            }
        });
        return ids;
    };

    const handleCheckAll = () => {
        setFlatRows((prev) => prev.map((r) => ({ ...r, checked: true })));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!challanId || !returnId || submitting) return;

        // Validate
        for (const row of flatRows) {
            if (row.checked && !row.inventory_product_id) {
                toast.error(`Select a serial number for each checked unit of "${row.product_name}".`);
                return;
            }
        }

        const keyToIds: Record<string, string[]> = {};
        for (const row of flatRows) {
            if (row.checked && row.inventory_product_id) {
                if (!keyToIds[row.key]) keyToIds[row.key] = [];
                keyToIds[row.key].push(row.inventory_product_id);
            }
        }
        for (const [, ids] of Object.entries(keyToIds)) {
            if (new Set(ids).size !== ids.length) {
                toast.error("Duplicate serial selected for the same accessory.");
                return;
            }
        }

        const services = accessoryReturnEditData?.services || [];
        const items = services
            .map((service: any) => ({
                sales_order_service_item_id: service.sales_order_service_item_id,
                accessories: (service.accessories || [])
                    .map((acc: any) => {
                        const key = accessoryKey(service.sales_order_service_item_id, acc);
                        const selectedIds = flatRows
                            .filter((r) => r.key === key && r.checked && r.inventory_product_id)
                            .map((r) => r.inventory_product_id);
                        if (!selectedIds.length) return null;
                        return {
                            accessory_id: acc.accessory_id,
                            product_id: acc.product_id,
                            return_qty: selectedIds.length,
                            return_inventory_product_ids: selectedIds,
                        };
                    })
                    .filter(Boolean),
            }))
            .filter((item: any) => item.accessories.length > 0);

        if (!items.length) {
            toast.error("Check at least one unit and select its serial number.");
            return;
        }

        setSubmitting(true);
        try {
            await dispatch(
                updateAccessoryReturn({
                    challanId,
                    returnId,
                    payload: { note: note.trim() || undefined, items },
                }),
            ).unwrap();
            toast.success("Accessory return updated successfully.");
            navigate(viewPath);
        } catch (error: any) {
            toast.error(
                typeof error === "string"
                    ? error
                    : error?.message || "Failed to update accessory return.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (loading && !accessoryReturnEditData) return <LoadingSpinner />;
    if (!accessoryReturnEditData) {
        return <div className="p-4 text-center">Return record not found.</div>;
    }

    return (
        <PageContainer>
            <PageBreadcrumb
                title="Edit Accessory Return"
                titleLink=""
                breadcrumbs={[
                    { label: "Sales Order Services", to: "/sales_order/service/sales_order_log" },
                    { label: "Manage Challan", to: listingPath },
                    { label: "View Return", to: viewPath },
                    { label: "Edit Return", to: "" },
                ]}
                backNavigationPath={viewPath}
            />
            <hr />

            <Row>
                <Col lg={12}>
                    <Card className="shadow-none">
                        <Card.Header>
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-2">
                                    <EditIcon style={{ color: MecordColors.ThemeBGColor }} />
                                    <h6 className="card-title mb-0">Edit accessory return</h6>
                                </div>
                                {flatRows.length > 0 && (
                                    <button
                                        type="button"
                                        disabled={submitting}
                                        onClick={handleCheckAll}
                                        className="btn btn-sm d-flex align-items-center gap-1"
                                        style={{
                                            backgroundColor: MecordColors.LightThemeBGColor,
                                            color: MecordColors.ThemeBGColor,
                                            fontSize: "0.8rem",
                                            borderRadius: "6px",
                                            padding: "6px 14px",
                                            border: `1px solid ${MecordColors.ThemeBGColor}`,
                                        }}
                                    >
                                        Check All
                                    </button>
                                )}
                            </div>
                        </Card.Header>

                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <p className="mb-2">
                                        <strong>Challan No:</strong> {accessoryReturnEditData.challan_no || "-"}
                                    </p>
                                    <p className="mb-0">
                                        <strong>SO No:</strong> {accessoryReturnEditData.sales_order_number || "-"}
                                    </p>
                                </Col>
                                <Col md={4}>
                                    <p className="mb-2">
                                        <strong>Challan Date:</strong>{" "}
                                        <FormattedDate date={accessoryReturnEditData.challan_date} />
                                    </p>
                                </Col>
                                <Col md={4}>
                                    <div
                                        style={{
                                            backgroundColor: "#fff8e6",
                                            border: "1px solid #f3d9a4",
                                            borderRadius: 6,
                                            padding: "8px 12px",
                                            fontSize: "0.82rem",
                                        }}
                                    >
                                        Check units and select serials for this batch. Unchecked units
                                        stay available for a later return. Inventory is restored only
                                        after GRN inward.
                                    </div>
                                </Col>
                            </Row>

                            <Form onSubmit={handleSubmit}>
                                {/* Flat table */}
                                <div className="table-responsive mb-4">
                                    {flatRows.length > 0 ? (
                                        <table
                                            style={{
                                                width: "100%",
                                                borderCollapse: "collapse",
                                                fontSize: "0.78rem",
                                            }}
                                        >
                                            <thead>
                                                <tr>
                                                    <th style={{ ...thStyle, textAlign: "center", width: 60 }}>Return</th>
                                                    <th style={thStyle}>S.No</th>
                                                    <th style={thStyle}>Accessory</th>
                                                    <th style={thStyle}>SKU</th>
                                                    <th style={thStyle}>Service Item</th>
                                                    <th style={{ ...thStyle, textAlign: "center" }}>Issued</th>
                                                    <th style={{ ...thStyle, textAlign: "center" }}>Other Batches</th>
                                                    <th style={{ ...thStyle, textAlign: "center" }}>Max (This Batch)</th>
                                                    <th style={{ ...thStyle, minWidth: 180 }}>Serial No</th>
                                                    <th style={{ ...thStyle, minWidth: 120 }}>Warranty</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {flatRows.map((row, idx) => {
                                                    const excluded = selectedIdsForKey(row.key, row.globalIdx);
                                                    const filteredOptions = row.available.filter(
                                                        (opt) =>
                                                            opt.inventory_product_id === row.inventory_product_id ||
                                                            !excluded.has(opt.inventory_product_id),
                                                    );
                                                    const selectedValue: ReturnUnitOption | null =
                                                        row.inventory_product_id
                                                        ? {
                                                              inventory_product_id: row.inventory_product_id,
                                                              serial_no:
                                                                  row.serial_no ||
                                                                  row.available.find(
                                                                      (o) =>
                                                                          o.inventory_product_id ===
                                                                          row.inventory_product_id,
                                                                  )?.serial_no ||
                                                                  "",
                                                              warranty_date:
                                                                  row.warranty_date ||
                                                                  row.available.find(
                                                                      (o) =>
                                                                          o.inventory_product_id ===
                                                                          row.inventory_product_id,
                                                                  )?.warranty_date ||
                                                                  null,
                                                          }
                                                        : null;

                                                    return (
                                                        <tr
                                                            key={`${row.key}-u-${row.unitIdx}`}
                                                            style={{
                                                                backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa",
                                                            }}
                                                        >
                                                            <td style={{ ...tdStyle, textAlign: "center", width: 60 }}>
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={row.checked}
                                                                    disabled={submitting}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked;
                                                                        updateRow(
                                                                            row.globalIdx,
                                                                            checked
                                                                                ? { checked: true }
                                                                                : {
                                                                                      checked: false,
                                                                                      inventory_product_id: "",
                                                                                      serial_no: "",
                                                                                      warranty_date: null,
                                                                                  },
                                                                        );
                                                                    }}
                                                                />
                                                            </td>
                                                            <td style={tdStyle}>{row.globalIdx}</td>
                                                            <td style={tdStyle}>
                                                                <span
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        color: MecordColors.ThemeBGColor,
                                                                    }}
                                                                >
                                                                    {row.product_name}
                                                                </span>
                                                            </td>
                                                            <td style={tdStyle}>{row.product_sku || "-"}</td>
                                                            <td style={tdStyle}>
                                                                <span>{row.service_name}</span>
                                                                {row.service_sku && (
                                                                    <span className="text-muted ms-1">
                                                                        {row.service_sku}
                                                                    </span>
                                                                )}
                                                                {row.service_sn && (
                                                                    <span className="text-muted ms-1">
                                                                        | SN: {row.service_sn}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td style={{ ...tdStyle, textAlign: "center" }}>
                                                                {row.issued_qty}
                                                            </td>
                                                            <td style={{ ...tdStyle, textAlign: "center" }}>
                                                                {row.other_returned_qty}
                                                            </td>
                                                            <td style={{ ...tdStyle, textAlign: "center" }}>
                                                                {row.remaining_qty}
                                                            </td>
                                                            <td style={{ ...tdStyle, minWidth: 180 }}>
                                                                <Autocomplete
                                                                    size="small"
                                                                    options={filteredOptions}
                                                                    disabled={submitting}
                                                                    getOptionLabel={(option) =>
                                                                        option.serial_no || ""
                                                                    }
                                                                    isOptionEqualToValue={(a, b) =>
                                                                        a.inventory_product_id ===
                                                                        b.inventory_product_id
                                                                    }
                                                                    value={selectedValue}
                                                                    onChange={(_e, selected) => {
                                                                        if (!selected) {
                                                                            updateRow(row.globalIdx, {
                                                                                checked: false,
                                                                                inventory_product_id: "",
                                                                                serial_no: "",
                                                                                warranty_date: null,
                                                                            });
                                                                            return;
                                                                        }
                                                                        updateRow(row.globalIdx, {
                                                                            checked: true,
                                                                            inventory_product_id:
                                                                                selected.inventory_product_id,
                                                                            serial_no: selected.serial_no,
                                                                            warranty_date:
                                                                                selected.warranty_date || null,
                                                                        });
                                                                    }}
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            placeholder="Select serial"
                                                                            sx={{
                                                                                "& .MuiInputBase-root": {
                                                                                    fontSize: "0.75rem",
                                                                                    backgroundColor: "#fff",
                                                                                    minHeight: 30,
                                                                                    padding: "0 4px",
                                                                                },
                                                                                "& .MuiInputBase-input": {
                                                                                    padding: "3px 6px !important",
                                                                                    fontSize: "0.75rem",
                                                                                },
                                                                                "& .MuiOutlinedInput-root": {
                                                                                    paddingTop: "0 !important",
                                                                                    paddingBottom: "0 !important",
                                                                                },
                                                                            }}
                                                                        />
                                                                    )}
                                                                />
                                                            </td>
                                                            <td style={tdStyle}>
                                                                {row.warranty_date ||
                                                                    row.available.find(
                                                                        (o) =>
                                                                            o.inventory_product_id ===
                                                                            row.inventory_product_id,
                                                                    )?.warranty_date ||
                                                                    "—"}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div
                                            className="d-flex flex-column align-items-center justify-content-center"
                                            style={{ minHeight: 200, color: "#aaa", userSelect: "none" }}
                                        >
                                            <svg
                                                width="60"
                                                height="60"
                                                viewBox="0 0 72 72"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                style={{ marginBottom: 12, opacity: 0.2 }}
                                            >
                                                <rect
                                                    x="12"
                                                    y="8"
                                                    width="48"
                                                    height="56"
                                                    rx="4"
                                                    stroke="#888"
                                                    strokeWidth="3"
                                                />
                                                <line x1="22" y1="24" x2="50" y2="24" stroke="#888" strokeWidth="2.5" strokeLinecap="round" />
                                                <line x1="22" y1="34" x2="50" y2="34" stroke="#888" strokeWidth="2.5" strokeLinecap="round" />
                                                <line x1="22" y1="44" x2="38" y2="44" stroke="#888" strokeWidth="2.5" strokeLinecap="round" />
                                            </svg>
                                            <p style={{ fontSize: "0.88rem", marginBottom: 0 }}>
                                                No units available for this batch
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Note */}
                                <Form.Group className="mb-4" controlId="editReturnNote">
                                    <Form.Label style={{ fontWeight: 600, fontSize: "0.85rem" }}>
                                        Note
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        maxLength={500}
                                        placeholder="Optional note for this return"
                                        value={note}
                                        disabled={submitting}
                                        onChange={(e) => setNote(e.target.value)}
                                    />
                                </Form.Group>

                                <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
                                    <CancelButton
                                        onClick={() => navigate(viewPath)}
                                        disabled={submitting}
                                        showConfirmation={false}
                                    />
                                    <PrimarySubmitButton
                                        label={`Update Return${totalReturnQty ? ` (${totalReturnQty})` : ""}`}
                                        isLoading={submitting}
                                        loadingLabel="Updating..."
                                        disabled={submitting || totalReturnQty <= 0}
                                    />
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageContainer>
    );
};

export default EditReturnAccessories;
